import * as React from "react";
import * as Sentry from "@sentry/react";

import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import {
  redactRumError,
  scrubSentryEvent,
} from "@verse_core/shared_components/utils/sentryScrub";

import { datadogRum } from "@datadog/browser-rum";

declare global {
  interface Window {
    Playwright?: boolean;
  }
}

function initTrackers() {
  if (!window.location.host.includes("localhost") && !window.Playwright) {
    datadogRum.init({
      clientToken: "pubd52a0721fd1d9a77fb56a2e3bdc0d730",
      applicationId: "76eeaad1-6bd6-40c6-a4b8-a00acbeb8eeb",
      site: "us5.datadoghq.com",
      service: "verse-medical",
      env:
        import.meta.env.VITE_APP_ENV || import.meta.env.MODE || "development",
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      // Click action names take the clicked element's text; this makes them
      // honor `data-dd-privacy="mask"` instead of always sending it.
      enablePrivacyForActionName: true,
      trackResources: true,
      trackLongTasks: true,
      // The patient portal is recorded unmasked too (Datadog BAA in place).
      // An element can still opt out with `data-dd-privacy="mask"`.
      defaultPrivacyLevel: "allow",
      startSessionReplayRecordingManually: true,
      beforeSend: (event) => {
        // /go-link-login carries a login token in the fragment
        if (event.view.url.includes("/go-link-login#")) {
          event.view.url = event.view.url.split("#")[0];
        }
        if (event.type === "error") {
          redactRumError(event.error);
        }
        return true;
      },
    });
    datadogRum.startSessionReplayRecording();

    Sentry.init({
      dsn: "https://ffe37f3d3fab128c0a54841a4ed766da@o4504764895789056.ingest.us.sentry.io/4509487623503873",
      environment:
        import.meta.env.VITE_APP_ENV || import.meta.env.MODE || "development",
      release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
      sendDefaultPii: false,
      integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
      tracesSampleRate: 1.0,
      beforeSend(event) {
        // Scrub sensitive data from the event before sending it to Sentry.
        scrubSentryEvent(event);

        // We do not scrub the user object itself. Sentry only ever identifies
        // clinician accounts: the patient portal has its own auth domain and
        // never calls Sentry.setUser, so no patient identity reaches this event.

        // In development, print all events to the console.
        if (import.meta.env.VITE_APP_ENV === "development") {
          console.error("Sentry Event:", event);
        }
        return event;
      },
      beforeSendTransaction(event) {
        // Page loads and route changes carry URLs and request spans.
        scrubSentryEvent(event);

        return event;
      },
    });
  }
}

export default initTrackers;
